import { UIElement } from '../ui-element';
import type {
    UISectionBackgroundColors,
    UISectionLayouts,
    UISectionTopMarginSizes,
} from './ui-section.type';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import styles from './ui-section.css';
import { Mapped } from '../../global/constants';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UISection
 * @element ui-section
 * @classdesc Represents a class for <code>ui-section</code> element.
 * @property {UISectionSurfaceColor} [background="neutral"] {@attr background} Sets background surface color for section.
 *  {@desc neutral: Neutral color - Default}
 *  {@desc primary: Primary color}
 *  {@desc secondary: Secondary color}
 *  {@desc tertiary: Tertiary color}
 * @property {UISectionLayouts} [layout="vertical"] {@attr layout} Sets layout direction for section. Horizontal applies only in desktop.
 *  {@desc vertical: Vertical layout - Default}
 *  {@desc horizontal: Horizontal layout}
 * @property {UISectionTopMarginSizes} [topMarginSize="large"] {@attr top-margin-size} Sets top margin height for section.
 * {@desc large: Large margin - Default}
 * {@desc small: Small margin}
 * @property {string} [heading=""] {@attr heading} Main heading for section.
 * @property {string} [subheading=""] {@attr subheading} Sub heading for section.
 * @example
 * <ui-section heading="Your accounts" subheading="Manage your accounts and cards" layout="horizontal" background="primary" top-margin-size="small">
 *   <ui-assets>
 *     <ui-asset label="Main account" label-secondary="EE345678909870901345">
 *       <ui-badge layout="pin" color="pineapple">Default</ui-badge>
 *       <span slot="value"><ui-currency value="1200.48"></ui-currency> EUR</span>
 *     </ui-asset>
 *     <ui-asset
 *       label="Randy Lahey"
 *       label-secondary="EE323601345251978909"
 *       value-secondary="+2 currencies"
 *     >
 *       <span slot="value"><ui-currency value="12870.72"></ui-currency> EUR</span>
 *     </ui-asset>
 *     <ui-asset
 *       label="Credit card account"
 *       label-secondary="EE319781678251782328"
 *     >
 *       <span slot="value"><ui-currency value="900.35"></ui-currency> EUR</span>
 *     </ui-asset>
 *   </ui-assets>
 * </ui-section>
 */
@Component(
    {
        tag: 'ui-section',
        styles,
    },
    Mapped.True
)
export class UISection extends UIElement {
    /**
     * Background surface color options for section.
     * @type {UISectionSurfaceColors}
     * @readonly
     */
    static readonly backgrounds: UISectionBackgroundColors = {
        Neutral: 'neutral',
        Primary: 'primary',
        Secondary: 'secondary',
        Tertiary: 'tertiary',
    };

    /**
     * Layout options for section.
     * @type {UISectionLayouts}
     * @readonly
     */
    static readonly layouts: UISectionLayouts = {
        Vertical: 'vertical' /* Default */,
        Horizontal: 'horizontal' /* Only applies for Desktop version */,
    };

    /**
     * Top margin size options for section.
     * @type {UISectionTopMarginSizes}
     * @readonly
     */
    static readonly topMarginSizes: UISectionTopMarginSizes = {
        Large: 'large' /* Default */,
        Small: 'small',
    };

    @Attribute(String, Mapped.True) background = UISection.backgrounds.Neutral;
    @Attribute(String, Mapped.True) layout = UISection.layouts.Vertical;
    @Attribute(String) topMarginSize = UISection.topMarginSizes.Large;
    @Attribute(String, Mapped.True) heading = '';
    /* Subheading only shown if heading also present */
    @Attribute(String, Mapped.True) subheading = '';
    @Child('.ui-section__heading-wrapper') button!: HTMLButtonElement;
    @Child('.ui-section__content-wrapper') icon!: HTMLElement;

    /**
     * Get section headings if provided, with proper wrappers for styling.
     * @returns {HTMLElement | null}
     */
    getSectionHeadings(): HTMLElement | null {
        const mainHeadingText = this.heading;

        // Do not render any headings if main one is not provided
        if (!mainHeadingText) {
            return null;
        }

        // Wrap heading texts into elements
        const mainHeadingElement = this.createElement({
            tagName: 'h2',
            classList: { 'ui-section__main-heading': true },
            attributes: {},
            children: [mainHeadingText],
        });
        const subheadingText = this.subheading;
        const subHeadingElement = subheadingText
            ? this.createElement({
                  tagName: 'p',
                  classList: { 'ui-section__sub-heading': true },
                  attributes: {},
                  children: [subheadingText],
              })
            : null;

        // Wrap headings elements into common inner wrapper for alignment
        const headings: Array<HTMLElement> = [
            mainHeadingElement,
            subHeadingElement,
        ].filter(Boolean) as Array<HTMLElement>; // Exclude subheading if null
        const headingsInnerWrapper = this.createElement({
            tagName: 'div',
            classList: { 'ui-section__heading-inner-wrapper': true },
            attributes: {},
            children: [...headings],
        });

        // Wrap inner wrapper into outer wrapper for positioning in grid
        return this.createElement({
            tagName: 'div',
            classList: { 'ui-section__heading-wrapper': true },
            attributes: {},
            children: [headingsInnerWrapper],
        });
    }

    /**
     * Get the whole content of section including headings and children.
     * @returns {HTMLElement | null}
     */
    getContent(): HTMLElement | null {
        const headingsWrapper = this.getSectionHeadings();

        // Do not render section at all if no headings and no children provided
        if (!headingsWrapper && !this.children.length) {
            return null;
        }
        // Wrap children into content wrapper if provided
        const childrenContentWrapper = this.children.length
            ? this.createElement({
                  tagName: 'div',
                  classList: { 'ui-section__content-wrapper': true },
                  attributes: [],
                  children: [...this.children],
              })
            : null;

        // Wrap headings and content wrappers into main section container
        const sectionElements: Array<HTMLElement> = [
            headingsWrapper,
            childrenContentWrapper,
        ].filter(Boolean) as Array<HTMLElement>; // Exclude elements that are null
        return this.createElement({
            tagName: 'section',
            classList: { 'ui-section__container': true },
            attributes: {},
            children: [...sectionElements],
        });
    }

    /**
     * @inheritDoc
     */
    render() {
        const sectionContent = this.getContent();

        if (sectionContent) {
            this.detachChildNodes();
            this.append(sectionContent);
            return;
        }
    }
}
